import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Image, X, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { createWriter, updateWriter, getAdminWriters, verifyAdmin, uploadToCloudinary, getAccounts, createAccount, updateAccount } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminWriterEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = id && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [cred, setCred] = useState({ username: '', password: '' });
  const [credSaving, setCredSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    image_url: ''
  });

  const fetchWriter = useCallback(async () => {
    if (!isEditing) {
      setInitialLoading(false);
      return;
    }

    try {
      const writers = await getAdminWriters();
      const writer = writers.find(w => w.id === id);
      if (writer) {
        // Only hydrate the form once; never overwrite in-progress edits
        setFormData(prev => (prev.name || prev.bio || prev.image_url) ? prev : {
          name: writer.name || '',
          bio: writer.bio || '',
          image_url: writer.image_url || ''
        });
      } else {
        toast.error('Writer not found');
        navigate('/admin/writers');
      }
    } catch (error) {
      toast.error('Failed to load writer');
      navigate('/admin/writers');
    } finally {
      setInitialLoading(false);
    }
  }, [id, isEditing, navigate]);

  useEffect(() => {
    const checkAuth = async () => {
      const user = await verifyAdmin();
      if (!user) {
        navigate('/admin');
        return;
      }
      if (user.role && user.role !== 'admin') {
        navigate('/admin/dashboard');
        return;
      }
      if (isEditing) {
        try {
          const accs = await getAccounts();
          setAccount(accs.find(a => a.writer_id === id) || null);
        } catch (e) {
          console.error('Failed to load accounts', e);
        }
      }
      fetchWriter();
    };
    checkAuth();
  }, [navigate, fetchWriter]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const result = await uploadToCloudinary(file);
      setFormData({ ...formData, image_url: result.secure_url });
      toast.success('Photo uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setLoading(true);
    try {
      let writerId = id;
      if (isEditing) {
        await updateWriter(id, formData);
      } else {
        const created = await createWriter(formData);
        writerId = created.id;
      }

      // Optionally create login credentials in the same save
      if (!account && cred.username.trim() && cred.password) {
        try {
          await createAccount({ username: cred.username, password: cred.password, writer_id: writerId });
          toast.success(isEditing ? 'Writer updated & login created' : 'Writer created with login');
        } catch (credError) {
          toast.error(credError.response?.data?.detail || 'Writer saved, but login creation failed');
          setLoading(false);
          return;
        }
      } else {
        toast.success(isEditing ? 'Writer updated' : 'Writer created');
      }
      navigate('/admin/writers');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save writer');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLogin = async (enabled) => {
    try {
      const updated = await updateAccount(account.id, { disabled: !enabled });
      setAccount(updated);
      toast.success(enabled ? 'Login enabled' : 'Login disabled');
    } catch (error) {
      toast.error('Failed to update login status');
    }
  };

  const handleResetPassword = async () => {
    if (!cred.password || cred.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setCredSaving(true);
    try {
      await updateAccount(account.id, { password: cred.password });
      setCred({ ...cred, password: '' });
      toast.success('Password updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setCredSaving(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-writer-editor">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-surface border-b border-zinc-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/admin/writers"
              className="flex items-center gap-2 text-text-secondary hover:text-white transition-colors"
              data-testid="back-to-writers"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-body">Writers</span>
            </Link>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-[#CCFF00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide"
              data-testid="save-writer"
            >
              {loading ? (
                <span className="spinner border-black border-t-transparent w-4 h-4" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Update' : 'Create'}
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading text-3xl font-black text-white uppercase mb-8">
          {isEditing ? 'Edit Writer' : 'New Writer'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Profile Photo */}
          <div>
            <Label className="text-white">Profile Photo</Label>
            <div className="mt-2 flex items-center gap-6">
              {formData.image_url ? (
                <div className="relative">
                  <img
                    src={formData.image_url}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover border-2 border-zinc-700"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, image_url: '' })}
                    className="absolute -top-1 -right-1 p-1 bg-black border border-zinc-700 rounded-full text-white hover:bg-red-500 transition-colors"
                    data-testid="remove-writer-image"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label className="w-24 h-24 rounded-full bg-surface border-2 border-dashed border-zinc-700 hover:border-[#CCFF00] cursor-pointer flex items-center justify-center transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="writer-image-upload"
                  />
                  {uploading ? (
                    <div className="spinner w-6 h-6" />
                  ) : (
                    <Image className="w-8 h-8 text-text-secondary" />
                  )}
                </label>
              )}
              <div>
                <p className="font-body text-sm text-white">Upload a profile photo</p>
                <p className="font-mono text-xs text-text-secondary mt-1">JPG, PNG, WebP. Max 10MB</p>
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-white text-lg">Name</Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Writer's full name..."
              className="mt-2 h-14 text-xl font-heading font-bold bg-transparent border-zinc-800 text-white focus:border-[#CCFF00]"
              data-testid="writer-name-input"
            />
          </div>

          {/* Bio */}
          <div>
            <Label htmlFor="bio" className="text-white text-lg">Bio</Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="A short write-up about this writer..."
              rows={5}
              className="mt-2 bg-surface border-zinc-800 text-white focus:border-[#CCFF00] resize-none font-body"
              data-testid="writer-bio-input"
            />
          </div>

          {/* Login Credentials */}
          <div className="bg-surface border border-zinc-800 p-6" data-testid="writer-credentials-section">
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="w-4 h-4 text-[#CCFF00]" />
              <Label className="text-white text-lg">Login Credentials</Label>
            </div>

            {account ? (
              <div className="space-y-5 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs text-text-secondary uppercase">Username</p>
                    <p className="font-body font-medium text-white mt-1" data-testid="writer-account-username">{account.username}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-mono text-xs uppercase ${account.disabled ? 'text-red-400' : 'text-[#CCFF00]'}`} data-testid="writer-login-status">
                      {account.disabled ? 'Disabled' : 'Enabled'}
                    </span>
                    <Switch
                      checked={!account.disabled}
                      onCheckedChange={handleToggleLogin}
                      data-testid="writer-login-toggle"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    value={cred.password}
                    onChange={(e) => setCred({ ...cred, password: e.target.value })}
                    placeholder="New password (min 6 characters)"
                    className="bg-black border-zinc-800 text-white focus:border-[#CCFF00]"
                    data-testid="writer-new-password-input"
                  />
                  <Button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={credSaving}
                    variant="outline"
                    className="border-zinc-800 text-white hover:bg-[#CCFF00] hover:text-black hover:border-[#CCFF00] shrink-0"
                    data-testid="writer-reset-password-button"
                  >
                    {credSaving ? 'Saving...' : 'Update Password'}
                  </Button>
                </div>
                <p className="font-mono text-xs text-text-secondary">Disabling blocks this writer from logging in without deleting their profile or articles.</p>
              </div>
            ) : (
              <div className="mt-4">
                <p className="font-body text-sm text-text-secondary mb-4">
                  Optional — give this writer their own login at <span className="text-[#CCFF00] font-mono">/admin</span>. Leave blank to skip.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    type="text"
                    value={cred.username}
                    onChange={(e) => setCred({ ...cred, username: e.target.value })}
                    placeholder="Username"
                    autoComplete="off"
                    className="bg-black border-zinc-800 text-white focus:border-[#CCFF00]"
                    data-testid="writer-cred-username-input"
                  />
                  <Input
                    type="password"
                    value={cred.password}
                    onChange={(e) => setCred({ ...cred, password: e.target.value })}
                    placeholder="Password (min 6 characters)"
                    autoComplete="new-password"
                    className="bg-black border-zinc-800 text-white focus:border-[#CCFF00]"
                    data-testid="writer-cred-password-input"
                  />
                </div>
                <p className="font-mono text-xs text-text-secondary mt-3">The login is created when you hit {isEditing ? 'Update' : 'Create'}.</p>
              </div>
            )}
          </div>
        </form>
      </main>
    </div>
  );
}
